// MA-703 — `take_away_blank` renderer, for a `sub` model whose MINUEND or
// SUBTRAHEND (not the remainder) is the authored blank: "5 − ___ = 1" or
// "___ − 5 = 1". Neither shape is a plain take-away (TakeAwayRenderer.tsx),
// which always crosses `b` of `a` dots and CAPTIONS BOTH operands as
// numerals — captioning the blank operand here would print the answer as a
// numeral, and crossing dots by an unknown count has nothing to cross by.
//
// TWO answer-safe shapes, selected by the `blank` prop:
//
//   blank === "subtrahend" (a − ___ = c): draws all `a` KNOWN objects,
//     NONE crossed out — the take-away amount is never drawn as a count at
//     all, not even as crossed dots — plus ONE outlined, dashed take-away
//     BOX (rounded rectangle, "?" centred), separated from the counter grid
//     by a real gap so it cannot read as an (a+1)th counter (R4, operator
//     ruling round 1, 2026-09-21 — RED must-fix 4: the first cut placed the
//     "?" slot ON the grid pitch, as the very next dot position, which read
//     as an 11th object — addition grammar, not take-away). Caption: "a − ?".
//
//   blank === "minuend" (___ − b = c): draws `a` (= impliedAnswer, the full
//     minuend) objects with the known `b` LAST of them crossed — visually
//     identical to TakeAwayRenderer's normal layout (first a−b intact, last
//     b crossed). Counting every object DOES give the answer (the block's
//     own sketch sanctions this as the concrete-band strategy) — what must
//     never happen is the ANSWER appearing as a NUMERAL. Caption: "? − b".
//
// ANSWER-SAFETY BY CONSTRUCTION: the caption never receives the blank
// operand's real value — it is passed `undefined`, and `numeralCard`
// (StagedMathRenderers.tsx) already renders "?" for any non-finite value.
// For blank==="subtrahend" the mapper additionally never sends the real
// (unknown) subtrahend value in the payload at all (`b` is a bookkeeping
// 0, contentToEngineItem.ts's MA-703 comment) — there is no code path here
// that could read or draw it.
//
// NOT registered as `take_away` (mathVisualStage.ts's STAGED_KINDS would
// project it to `count_back` past "concrete", which PRINTS its `a` prop as
// a numeral card — a real leak for blank==="minuend", where `a` IS the
// answer). `take_away_blank` is its own, unstaged kind — same reasoning as
// `ten_frame_sub`'s exclusion: the authored model IS the picture at every
// band, not an early counted-all stand-in.
import type { MathRendererProps } from "./rendererRegistry";
import { OperandCaption, operandCaptionSize } from "./StagedMathRenderers";

const DOT_INTACT = "var(--pal-orange, #d97742)";
const DOT_REMOVED_BG = "var(--color-bg-secondary, #f5efe3)";
const DOT_REMOVED_STROKE = "var(--color-border-secondary, #cdbfae)";
const X_COLOR = "var(--color-error, #c0392b)";
const UNKNOWN_STROKE = "var(--color-border-secondary, #cdbfae)";
const UNKNOWN_TEXT = "var(--color-text-secondary, #7a6f60)";

const R = 13;
const CELL = 34;
const PAD = 18;
const COLS = 5;
const MAX_A = 20; // mirrors TakeAwayRenderer's safety clamp
const CAPTION_GAP = 14;
// R4 — the take-away BOX (blank-subtrahend only): a rounded rectangle,
// dashed outline, "?" centred, laid out BESIDE the counter grid (never on
// its grid pitch — a circle at the next dot position read as an (a+1)th
// counter, addition grammar not take-away) and separated from it by a real
// gap so it cannot be miscounted as one more object.
const BOX_SIZE = 34; // same visual footprint as one CELL, but square/dashed/rounded — never a circle
const BOX_GAP = 28; // >= 28px, per the operator ruling
const BOX_STROKE = 2; // the box's own stroke-width — half of this straddles OUTSIDE its nominal x/y/width/height box
// R6 (operator ruling round 2, 2026-09-21 — RED round-2 must-fix 1): the old
// `dotsWidth`/`dotsHeight` reserved exactly the box's nominal width/height and
// NOTHING for its own stroke — so the outer half of the 2px dashed stroke sat
// OUTSIDE the viewBox on every real blank-subtrahend item (measured: 1px
// clipped on the right at a=3/7/10, 0px bottom margin at a<=5), and SVG clips
// to its viewport, so the app visibly sheared off the box's right edge and
// rounded corner. BOX_MARGIN is real, asserted air beyond the stroke, not
// just "no clip" — >= 4px, per the ruling.
const BOX_MARGIN = 4; // >= 4px beyond the box's outer (stroke-inclusive) edge, per the operator ruling

function TakeAwayBlankRenderer({ a, b, blank }: MathRendererProps) {
  const safeA = Math.max(
    0,
    Math.min(MAX_A, Math.floor(Number.isFinite(a ?? NaN) ? (a as number) : 0)),
  );
  const isSubtrahendBlank = blank === "subtrahend";
  // Crossed count: 0 for a blank subtrahend (nothing is ever crossed — the
  // take-away amount is outlined, not drawn); the known `b` for a blank
  // minuend (identical crossing shape to the plain take_away renderer).
  const safeB = isSubtrahendBlank
    ? 0
    : Math.max(0, Math.min(safeA, Math.floor(Number.isFinite(b ?? NaN) ? (b as number) : 0)));

  // The counter grid draws ONLY the `a` known objects — no extra slot on
  // its pitch (R4). The take-away BOX (blank-subtrahend only) is laid out
  // as a separate element, offset past the grid's own width by BOX_GAP.
  const rows = Math.max(1, Math.ceil((safeA || 1) / COLS));
  const cols = Math.min(COLS, safeA || 1);
  const dotsGridWidth = PAD * 2 + (cols - 1) * CELL;
  const dotsHeightBase = PAD * 2 + (rows - 1) * CELL;
  // Box vertically centred on the counter grid's own row of dots (dots sit
  // on row centres at PAD, PAD+CELL, …; the grid's own vertical extent is
  // dotsHeightBase — 2*R tall at its narrowest, so centring the box on the
  // FIRST row keeps it level with a single-row grid and still clear of a
  // multi-row one).
  const boxX = dotsGridWidth + BOX_GAP;
  const boxY = PAD - BOX_SIZE / 2;
  // R6 — reserve the box's FULL outer extent (its nominal box PLUS half its
  // own stroke) plus a real BOX_MARGIN of air, on both axes, so nothing sits
  // outside the viewBox regardless of row count (width) or single-vs-multi
  // row grid (height).
  const boxOuterRight = boxX + BOX_SIZE + BOX_STROKE / 2;
  const boxOuterBottom = boxY + BOX_SIZE + BOX_STROKE / 2;
  const dotsWidth = isSubtrahendBlank ? Math.max(dotsGridWidth, boxOuterRight + BOX_MARGIN) : dotsGridWidth;
  const dotsHeight = isSubtrahendBlank ? Math.max(dotsHeightBase, boxOuterBottom + BOX_MARGIN) : dotsHeightBase;

  const cap = operandCaptionSize();
  const svgWidth = Math.max(dotsWidth, cap.width);
  const svgHeight = cap.height + CAPTION_GAP + dotsHeight;
  const dotsOffsetX = (svgWidth - dotsWidth) / 2;
  const capOffsetX = (svgWidth - cap.width) / 2;
  const dotsOffsetY = cap.height + CAPTION_GAP;

  const firstRemoved = safeA - safeB;
  const xArm = (R * 0.6).toFixed(1);

  // ANSWER-SAFETY: the caption's blank-side operand is always `undefined`
  // — numeralCard renders "?" for any non-finite value — never the real
  // (unknown) number, regardless of what `a`/`b` were passed for drawing.
  const capA = isSubtrahendBlank ? safeA : undefined;
  const capB = isSubtrahendBlank ? undefined : safeB;

  // ANSWER-SAFETY (MA-703 registry-audit finding): the aria-label is a
  // SEPARATE leak surface from the drawn/captioned numerals above — a
  // screen reader hears this string regardless of what's visually drawn.
  // For blank==="minuend", `safeA` (the dot count) IS the answer, so it
  // must NEVER appear here as a number; describe the picture only in
  // terms of the two KNOWN quantities (the crossed-out amount `safeB` and
  // the remaining amount `firstRemoved`, both given in the prompt). For
  // blank==="subtrahend", `safeA` is the KNOWN starting count (the answer
  // is the take-away amount, never stated at all), so it's safe to say.
  const ariaLabel = isSubtrahendBlank
    ? `${safeA} objects, take away an unknown amount.`
    : `An unknown number of objects, with ${safeB} taken away, leaving ${firstRemoved}.`;

  return (
    <svg
      viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      width="100%"
      style={{ maxWidth: Math.min(360, svgWidth) }}
      role="img"
      aria-label={ariaLabel}
    >
      <OperandCaption a={capA} b={capB} op="-" x={capOffsetX} y={0} />
      <g transform={`translate(${dotsOffsetX}, ${dotsOffsetY})`}>
        {Array.from({ length: safeA }).map((_, i) => {
          const cx = PAD + (i % COLS) * CELL;
          const cy = PAD + Math.floor(i / COLS) * CELL;
          const removed = i >= firstRemoved;
          if (removed) {
            return (
              <g key={i}>
                <circle cx={cx} cy={cy} r={R} fill={DOT_REMOVED_BG} stroke={DOT_REMOVED_STROKE} strokeWidth="1.5" />
                <line x1={cx - +xArm} y1={cy - +xArm} x2={cx + +xArm} y2={cy + +xArm} stroke={X_COLOR} strokeWidth="2.5" strokeLinecap="round" />
                <line x1={cx + +xArm} y1={cy - +xArm} x2={cx - +xArm} y2={cy + +xArm} stroke={X_COLOR} strokeWidth="2.5" strokeLinecap="round" />
              </g>
            );
          }
          return <circle key={i} cx={cx} cy={cy} r={R} fill={DOT_INTACT} />;
        })}
        {isSubtrahendBlank && (
          // R4 — the take-away BOX: a rounded rectangle, dashed outline,
          // "?" centred — visually distinct from every counter (not a
          // circle, not on the grid's column/row pitch), offset past the
          // grid by BOX_GAP (>= 28px) so it reads as a SEPARATE unknown
          // quantity being taken away, never as one more counted object.
          <g key="unknown-box">
            <rect
              x={boxX}
              y={boxY}
              width={BOX_SIZE}
              height={BOX_SIZE}
              rx={8}
              fill="none"
              stroke={UNKNOWN_STROKE}
              strokeWidth={BOX_STROKE}
              strokeDasharray="4 3"
            />
            <text
              x={boxX + BOX_SIZE / 2}
              y={boxY + BOX_SIZE / 2 + 5}
              textAnchor="middle"
              fontSize="16"
              fontWeight="700"
              fill={UNKNOWN_TEXT}
            >
              ?
            </text>
          </g>
        )}
      </g>
    </svg>
  );
}

export default TakeAwayBlankRenderer;
